import * as React from "react";
import {
  Typography,
  Box,
  Container,
  Button,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
  Grid,
} from "@mui/material";
import { connect } from "react-redux";
import { MainInput, main_button } from "../app/components/Styles";
import { postRequest } from "../core/network";
import { useTranslation } from "react-i18next";

export const Contact = ({ user }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();

  const [form, setForm] = React.useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = React.useState(false);
  const [snack, setSnack] = React.useState({ open: false, message: "", severity: "success" });

  React.useEffect(() => {
    if (user?.name || user?.email || user?.vendor?.brandName) {
      setForm((prev) => ({
        ...prev,
        ...(user.name && { name: user.name }),
        ...(user.email && { email: user.email }),
        ...(user.vendor?.brandName && { company: user.vendor.brandName }),
      }));
    }
  }, [user?.name, user?.email, user?.vendor?.brandName]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSnack((prev) => ({ ...prev, open: false }));
    try {
      await postRequest(
        {
          name: form.name,
          email: form.email,
          company: form.company,
          phone: form.phone,
          subject: form.subject,
          message: form.message,
        },
        undefined,
        undefined,
        undefined,
        "contact"
      );
      setSnack({
        open: true,
        message: t("contact.successMessage") || "Message sent. We'll get back to you soon.",
        severity: "success",
      });
      setForm({
        name: user?.name || "",
        email: user?.email || "",
        company: user?.vendor?.brandName || "",
        phone: "",
        subject: "",
        message: "",
      });
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || t("contact.errorMessage") || "Something went wrong. Please try again.";
      setSnack({ open: true, message, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnack = (_, reason) => {
    if (reason === "clickaway") return;
    setSnack((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ minHeight: "100%", pb: 4, backgroundColor: "var(--appBg)" }}>
      <Container
        maxWidth="md"
        sx={{
          px: { xs: 2, sm: 3 },
          pt: 4,
          pb: 2,
        }}
      >
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              color: "var(--secColor4)",
              fontWeight: 700,
              fontSize: isMobile ? "1.75rem" : "2rem",
              mb: 1,
              position: "relative",
              display: "inline-block",
            }}
          >
            {t("navigation.contactUs")}
            <Box
              component="span"
              sx={{
                position: "absolute",
                bottom: -4,
                left: 0,
                right: 0,
                height: 6,
                borderRadius: 1,
                backgroundColor: "var(--primaryColor)",
                opacity: 0.45,
                zIndex: 0,
              }}
            />
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "var(--secColor4)",
              opacity: 0.85,
              maxWidth: 560,
              mx: "auto",
              lineHeight: 1.65,
              mt: 2,
            }}
          >
            {t("contact.intro") || "Get in touch with us. Send your inquiry below and we'll respond as soon as we can."}
          </Typography>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            maxWidth: 640,
            mx: "auto",
            "& .MuiTextField-root": { mb: 2 },
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <MainInput
                required
                fullWidth
                name="name"
                label={t("contact.name") || "Name"}
                value={form.name}
                onChange={handleChange}
                autoComplete="name"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MainInput
                required
                fullWidth
                name="email"
                type="email"
                label={t("contact.email") || "Email"}
                value={form.email}
                onChange={handleChange}
                autoComplete="email"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MainInput
                fullWidth
                name="company"
                label={t("contact.company") || "Company"}
                value={form.company}
                onChange={handleChange}
                autoComplete="organization"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MainInput
                fullWidth
                name="phone"
                label={t("contact.phone") || "Phone"}
                value={form.phone}
                onChange={handleChange}
                autoComplete="tel"
              />
            </Grid>
            <Grid item xs={12}>
              <MainInput
                required
                fullWidth
                name="subject"
                label={t("contact.subject") || "Subject"}
                value={form.subject}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <MainInput
                required
                fullWidth
                name="message"
                label={t("contact.message") || "Message"}
                value={form.message}
                onChange={handleChange}
                multiline
                rows={5}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                fullWidth
                disabled={loading}
                sx={{ ...main_button, py: 1.5, mt: 1 }}
              >
                {loading ? t("common.submitting") || "Sending..." : t("contact.send") || "Send message"}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Container>

      <Snackbar
        open={snack.open}
        autoHideDuration={6000}
        onClose={handleCloseSnack}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => handleCloseSnack(null, "close")} severity={snack.severity} sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  user: state?.auth?.user,
});

export default connect(mapStateToProps)(Contact);
